import { useState, useEffect } from "react";
import { usePagedList } from "@/hooks/usePagedList";
import Pagination from "@/components/Pagination";
import { useNavigate } from "react-router-dom";
import { api, toast } from "../App";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandItem, CommandEmpty, CommandGroup } from "@/components/ui/command";
import { ChevronDown, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, FileText, DollarSign, Calendar, Download, Eye, Trash2 } from "lucide-react";
import { format } from "date-fns";
import DatePicker from "@/components/DatePicker";

export default function Invoices({ user }) {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [viewInvoiceDialogOpen, setViewInvoiceDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [projectBalance, setProjectBalance] = useState(null);

  // Helper function to check if user can view cost information
  const canViewCosts = () => {
    return user?.department === "Management" || 
           user?.department === "Sales" || 
           user?.department === "Accounts and Finance";
  };
  const [filters, setFilters] = useState({
    invoice_number: "",
    client_name: "",
    project_name: "",
    project_reference_invoice_number: "",
    aging: [] // Array for multiple aging selections
  });
  const [activeTab, setActiveTab] = useState("all");
  // Combobox open state for Client / Project filter dropdowns
  const [clientPickerOpen, setClientPickerOpen] = useState(false);
  const [projectPickerOpen, setProjectPickerOpen] = useState(false);
  // Link between an invoice being created and the proposal it originated from.
  // When the invoice save succeeds, the proposal is auto-deleted.
  const [linkedProposalId, setLinkedProposalId] = useState(null);
  const [rtiBump, setRtiBump] = useState(0);   // Force Ready-to-Invoice tab to reload
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    project_id: "",
    invoice_number: "",
    project_reference_invoice_number: "",
    description: "", // Kept for backward compatibility
    invoice_items: [{ description: "", amount: "" }],
    bill_to_name: "",
    issue_date: new Date().toISOString().split('T')[0],
    due_date: "",
    notes: ""
  });
  const [paymentData, setPaymentData] = useState({
    amount: "",
    payment_mode: "",
    bank_name: "",
    payment_reference: "",
    date_received: "",
    date_deposited: "",
    date_cleared: "",
    payment_workflow_status: "Received",
    writeoff_reason: ""
  });
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [paymentFormMode, setPaymentFormMode] = useState("add"); // "add" or "edit"
  const [viewingPayment, setViewingPayment] = useState(null); // payment object for details view

  useEffect(() => {
    fetchInvoices();
    fetchProjects();
    fetchClients();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await api.get("/invoices");
      setInvoices(response.data);
    } catch (error) {
      toast.error("Failed to fetch invoices");
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await api.get("/projects");
      setProjects(response.data);
    } catch (error) {
      console.error("Failed to fetch projects");
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get("/clients");
      setClients(response.data);
    } catch (error) {
      console.error("Failed to fetch clients");
    }
  };

  const fetchProjectBalance = async (projectId) => {
    if (!projectId) {
      setProjectBalance(null);
      return;
    }
    try {
      const response = await api.get(`/projects/${projectId}/invoice-balance`);
      setProjectBalance(response.data);
    } catch (error) {
      console.error("Failed to fetch project balance");
      setProjectBalance(null);
    }
  };

  // Calculate due date based on issue date + billing cycle days
  const calculateDueDate = (issueDate, billingCycleDays) => {
    if (!issueDate || !billingCycleDays) return "";
    const issue = new Date(issueDate);
    issue.setDate(issue.getDate() + billingCycleDays);
    return issue.toISOString().split('T')[0];
  };

  const handleProjectChange = async (projectId) => {
    setFormData({ ...formData, project_id: projectId });
    fetchProjectBalance(projectId);
    
    // Auto-populate bill_to_name and calculate due date from project's client
    try {
      const project = projects.find(p => p.id === projectId);
      if (project && project.client_id) {
        const clientResponse = await api.get(`/clients/${project.client_id}`);
        const client = clientResponse.data;
        const contacts = client.contacts || [];
        
        // Find billing contact
        let billingContact = contacts.find(contact => {
          const roles = contact.roles || [];
          return roles.some(role => role.toLowerCase().includes('billing'));
        });
        
        // Fallback to first contact if no billing contact
        if (!billingContact && contacts.length > 0) {
          billingContact = contacts[0];
        }
        
        // Set bill_to_name if contact found
        if (billingContact) {
          const contactName = `${billingContact.first_name || ''} ${billingContact.last_name || ''}`.trim();
          if (contactName) {
            setFormData(prev => ({ ...prev, bill_to_name: contactName }));
          }
        }
        
        // Calculate due date from issue date + billing cycle
        const billingCycleDays = client.billing_cycle_days || 30;
        const newDueDate = calculateDueDate(formData.issue_date, billingCycleDays);
        if (newDueDate) {
          setFormData(prev => ({ ...prev, due_date: newDueDate }));
        }
      }
    } catch (error) {
      console.error("Failed to fetch client data");
    }
  };

  // Line item management
  const addLineItem = () => {
    setFormData({
      ...formData,
      invoice_items: [...formData.invoice_items, { description: "", amount: "" }]
    });
  };

  const removeLineItem = (index) => {
    if (formData.invoice_items.length === 1) {
      toast.error("Must have at least one line item");
      return;
    }
    const newItems = formData.invoice_items.filter((_, i) => i !== index);
    setFormData({ ...formData, invoice_items: newItems });
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...formData.invoice_items];
    newItems[index][field] = value;
    setFormData({ ...formData, invoice_items: newItems });
  };

  const calculateTotal = () => {
    return formData.invoice_items.reduce((sum, item) => {
      const amount = parseFloat(item.amount) || 0;
      return sum + amount;
    }, 0);
  };

  const handleEdit = (invoice) => {
    setSelectedInvoice(invoice);
    setEditMode(true);
    setFormData({
      project_id: invoice.project_id,
      invoice_number: invoice.invoice_number,
      project_reference_invoice_number: invoice.project_reference_invoice_number || "",
      description: invoice.description || "",
      invoice_items: invoice.invoice_items && invoice.invoice_items.length > 0 
        ? invoice.invoice_items.map(item => ({ ...item, amount: item.amount.toString() }))
        : [{ description: invoice.description || "", amount: invoice.amount.toString() }],
      bill_to_name: invoice.bill_to_name || "",
      issue_date: new Date(invoice.issue_date).toISOString().split('T')[0],
      due_date: new Date(invoice.due_date).toISOString().split('T')[0],
      notes: invoice.notes || ""
    });
    fetchProjectBalance(invoice.project_id);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate line items
    if (formData.invoice_items.length === 0) {
      toast.error("Must have at least one line item");
      return;
    }
    
    for (let i = 0; i < formData.invoice_items.length; i++) {
      const item = formData.invoice_items[i];
      if (!item.description || !item.amount || parseFloat(item.amount) <= 0) {
        toast.error(`Line item ${i + 1}: Description and amount are required`);
        return;
      }
    }
    
    try {
      if (editMode) {
        // Update invoice
        const payload = {
          invoice_number: formData.invoice_number || undefined,
          project_reference_invoice_number: formData.project_reference_invoice_number || undefined,
          invoice_items: formData.invoice_items.map(item => ({
            description: item.description,
            amount: parseFloat(item.amount)
          })),
          issue_date: new Date(formData.issue_date).toISOString(),
          due_date: new Date(formData.due_date).toISOString(),
          notes: formData.notes
        };
        await api.patch(`/invoices/${selectedInvoice.id}`, payload);
        toast.success("Invoice updated successfully!");
      } else {
        // Create new invoice
        const payload = {
          project_id: formData.project_id,
          invoice_number: formData.invoice_number || undefined,
          project_reference_invoice_number: formData.project_reference_invoice_number || undefined,
          invoice_items: formData.invoice_items.map(item => ({
            description: item.description,
            amount: parseFloat(item.amount)
          })),
          issue_date: new Date(formData.issue_date).toISOString(),
          due_date: new Date(formData.due_date).toISOString(),
          notes: formData.notes
        };
        await api.post("/invoices", payload);
        toast.success("Invoice created successfully!");
        // Auto-delete the linked proposal (if this invoice was generated from
        // Ready-to-Invoice). Failure here is non-fatal — planner can still
        // manually delete the proposal.
        if (linkedProposalId) {
          try {
            await api.delete(`/proposed-invoices/${linkedProposalId}`);
            setRtiBump(n => n + 1);
          } catch {
            toast.warning("Invoice saved but linked proposal couldn't be removed — delete it manually.");
          }
          setLinkedProposalId(null);
        }
      }
      setDialogOpen(false);
      fetchInvoices();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editMode ? 'update' : 'create'} invoice`);
    }
  };

  const resetForm = () => {
    setProjectBalance(null);
    setEditMode(false);
    setSelectedInvoice(null);
    setLinkedProposalId(null);
    setFormData({
      project_id: "",
      invoice_number: "",
      project_reference_invoice_number: "",
      description: "",
      invoice_items: [{ description: "", amount: "" }],
      bill_to_name: "",
      issue_date: new Date().toISOString().split('T')[0],
      due_date: "",
      notes: ""
    });
  };

  const handleDialogOpenChange = (open) => {
    setDialogOpen(open);
    if (!open) {
      resetForm();
    }
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        amount: parseFloat(paymentData.amount),
        payment_mode: paymentData.payment_mode || null,
        bank_name: paymentData.bank_name || null,
        payment_reference: paymentData.payment_reference || null,
        date_received: paymentData.date_received ? new Date(paymentData.date_received).toISOString() : null,
        date_deposited: paymentData.date_deposited ? new Date(paymentData.date_deposited).toISOString() : null,
        date_cleared: paymentData.date_cleared ? new Date(paymentData.date_cleared).toISOString() : null,
        payment_workflow_status: paymentData.payment_workflow_status,
        writeoff_reason: paymentData.writeoff_reason || null
      };
      
      await api.post(`/invoices/${selectedInvoice.id}/payments`, payload);
      toast.success("Payment added successfully!");
      resetPaymentForm();
      await fetchInvoices();
      // Refresh selected invoice to show updated payment entries
      const response = await api.get(`/invoices`);
      const updatedInvoice = response.data.find(inv => inv.id === selectedInvoice.id);
      if (updatedInvoice) {
        setSelectedInvoice(updatedInvoice);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add payment");
    }
  };

  const handleEditPayment = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        amount: parseFloat(paymentData.amount),
        payment_mode: paymentData.payment_mode || null,
        bank_name: paymentData.bank_name || null,
        payment_reference: paymentData.payment_reference || null,
        date_received: paymentData.date_received ? new Date(paymentData.date_received).toISOString() : null,
        date_deposited: paymentData.date_deposited ? new Date(paymentData.date_deposited).toISOString() : null,
        date_cleared: paymentData.date_cleared ? new Date(paymentData.date_cleared).toISOString() : null,
        payment_workflow_status: paymentData.payment_workflow_status,
        writeoff_reason: paymentData.writeoff_reason || null
      };
      
      await api.patch(`/invoices/${selectedInvoice.id}/payments/${editingPaymentId}`, payload);
      toast.success("Payment updated successfully!");
      resetPaymentForm();
      await fetchInvoices();
      // Refresh selected invoice to show updated payment entries
      const response = await api.get(`/invoices`);
      const updatedInvoice = response.data.find(inv => inv.id === selectedInvoice.id);
      if (updatedInvoice) {
        setSelectedInvoice(updatedInvoice);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update payment");
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm("Are you sure you want to delete this payment entry?")) {
      return;
    }
    
    try {
      await api.delete(`/invoices/${selectedInvoice.id}/payments/${paymentId}`);
      toast.success("Payment deleted successfully!");
      await fetchInvoices();
      // Refresh selected invoice
      const response = await api.get(`/invoices`);
      const updatedInvoice = response.data.find(inv => inv.id === selectedInvoice.id);
      if (updatedInvoice) {
        setSelectedInvoice(updatedInvoice);
      }
    } catch (error) {
      toast.error("Failed to delete payment");
    }
  };

  const openPaymentDialog = (invoice) => {
    setSelectedInvoice(invoice);
    resetPaymentForm();
    setPaymentDialogOpen(true);
  };

  const resetPaymentForm = () => {
    setPaymentData({
      amount: "",
      payment_mode: "",
      bank_name: "",
      payment_reference: "",
      date_received: "",
      date_deposited: "",
      date_cleared: "",
      payment_workflow_status: "Received",
      writeoff_reason: ""
    });
    setEditingPaymentId(null);
    setPaymentFormMode("add");
  };

  const startEditingPayment = (payment) => {
    console.log("Starting to edit payment:", payment);
    
    setPaymentData({
      amount: payment.amount.toString(),
      payment_mode: payment.payment_mode || "",
      bank_name: payment.bank_name || "",
      payment_reference: payment.payment_reference || "",
      date_received: payment.date_received ? new Date(payment.date_received).toISOString().split('T')[0] : "",
      date_deposited: payment.date_deposited ? new Date(payment.date_deposited).toISOString().split('T')[0] : "",
      date_cleared: payment.date_cleared ? new Date(payment.date_cleared).toISOString().split('T')[0] : "",
      payment_workflow_status: payment.payment_workflow_status,
      writeoff_reason: payment.writeoff_reason || ""
    });
    setEditingPaymentId(payment.id);
    setPaymentFormMode("edit");
    
    console.log("Payment form mode set to: edit");
    console.log("Editing payment ID:", payment.id);
    
    // Scroll to the form section
    setTimeout(() => {
      const formElement = document.querySelector('form[data-payment-form]');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        console.log("Scrolled to form");
      } else {
        console.log("Form element not found");
      }
    }, 100);
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return "Unknown";
    const projectNumber = project.project_number || "";
    return projectNumber ? `${projectNumber} - ${project.name}` : project.name;
  };

  // Helper to format amount with currency label
  const formatAmountWithCurrency = (amount, currency) => {
    const curr = currency || "USD";
    return `${amount.toLocaleString()} ${curr}`;
  };

  // Get currency for an invoice from its project
  const getInvoiceCurrency = (invoice) => {
    return invoice.currency || "USD";
  };

  const getLastReminderDate = (invoice) => {
    // Prefer server-computed summary (list endpoint slims payment_reminders array)
    if (invoice.last_reminder_date) return invoice.last_reminder_date;
    if (!invoice.payment_reminders || invoice.payment_reminders.length === 0) {
      return null;
    }
    // Fallback: compute from full array (detail endpoint still returns it)
    const sortedReminders = [...invoice.payment_reminders].sort((a, b) => b.reminder_number - a.reminder_number);
    return sortedReminders[0].date;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "bg-green-500";
      case "paid_with_partial_writeoff":
        return "bg-green-400";
      case "partial_paid":
        return "bg-green-400";
      case "in_progress":
        return "bg-blue-500";
      case "partial_in_progress":
        return "bg-yellow-500";
      case "partial_writeoff":
        return "bg-orange-500";
      case "unpaid":
        return "bg-red-500";
      case "writeoff":
        return "bg-gray-600";
      default:
        return "bg-gray-500";
    }
  };

  const formatPaymentStatus = (status) => {
    const statusMap = {
      "paid": "Paid",
      "partial_paid": "Partial Paid",
      "in_progress": "In Progress",
      "partial_in_progress": "Partial (In Progress)",
      "unpaid": "Unpaid",
      "partial_writeoff": "Partial Write-off",
      "paid_with_partial_writeoff": "Paid w/partial WO",
      "writeoff": "Write-off"
    };
    return statusMap[status] || status;
  };

  // Revenue calculation with USD conversion for CAD invoices
  const getRevenueBreakdown = () => {
    let usdTotal = 0;
    let usdCollected = 0;
    let cadTotal = 0;
    let cadCollected = 0;
    let cadToUsdTotal = 0;
    let cadToUsdCollected = 0;

    invoices.forEach(inv => {
      const currency = inv.currency || "USD";
      if (currency === "CAD") {
        cadTotal += inv.amount || 0;
        cadCollected += inv.amount_paid || 0;
        // Use stored USD equivalent or calculate
        cadToUsdTotal += inv.usd_amount || (inv.amount * (inv.usd_conversion_rate || 0.74));
        cadToUsdCollected += inv.usd_amount_paid || (inv.amount_paid * (inv.usd_conversion_rate || 0.74));
      } else {
        usdTotal += inv.amount || 0;
        usdCollected += inv.amount_paid || 0;
      }
    });

    return {
      usdTotal,
      usdCollected,
      usdBalance: usdTotal - usdCollected,
      cadTotal,
      cadCollected,
      cadBalance: cadTotal - cadCollected,
      cadToUsdTotal,
      cadToUsdCollected,
      cadToUsdBalance: cadToUsdTotal - cadToUsdCollected,
      grandTotalUsd: usdTotal + cadToUsdTotal,
      grandCollectedUsd: usdCollected + cadToUsdCollected,
      grandBalanceUsd: (usdTotal + cadToUsdTotal) - (usdCollected + cadToUsdCollected)
    };
  };

  const getTotalRevenue = () => {
    const breakdown = getRevenueBreakdown();
    return breakdown.grandTotalUsd;
  };

  const getCollectedRevenue = () => {
    const breakdown = getRevenueBreakdown();
    return breakdown.grandCollectedUsd;
  };

  const getPendingRevenue = () => {
    const breakdown = getRevenueBreakdown();
    return breakdown.grandBalanceUsd;
  };

  const handleViewInvoice = async (invoice) => {
    try {
      const response = await api.get(`/invoices/${invoice.id}/details`);
      setInvoiceDetails(response.data);
      setSelectedInvoice(invoice);
      setViewInvoiceDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load invoice details");
    }
  };

  const handleDownloadPDF = async (invoiceId) => {
    try {
      const response = await api.get(`/invoices/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      
      if (!(response.data instanceof Blob)) {
        throw new Error("Invalid response type");
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const project = projects.find(p => p.id === selectedInvoice?.project_id);
      const projectName = project ? project.name.replace(/ /g, '_') : 'Invoice';
      const projectRef = project?.project_number || 'REF';
      link.setAttribute('download', `${projectName}_${projectRef}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Invoice PDF downloaded successfully!");
    } catch (error) {
      console.error("PDF download error:", error);
      toast.error("Failed to download invoice PDF");
    }
  };

  const handleDownloadExcel = async () => {
    try {
      const response = await api.get("/export/invoices", {
        responseType: 'blob'
      });
      
      // Check if response is actually a blob
      if (!(response.data instanceof Blob)) {
        throw new Error("Invalid response type");
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoices_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url); // Clean up
      toast.success("Invoices log downloaded successfully!");
    } catch (error) {
      console.error("Excel download error:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Failed to download invoices log";
      toast.error(errorMessage);
    }
  };

  // Helper functions for filtering
  const getClientName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return "Unknown";
    const client = clients.find(c => c.id === project.client_id);
    return client ? (client.client_name || client.company || "Unknown") : "Unknown";
  };

  const isOverdue = (dueDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    return due < today;
  };

  const isDueToday = (dueDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    return due.getTime() === today.getTime();
  };

  // Calculate aging status
  const getAgingStatus = (dueDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    
    // No Due: until due date (inclusive)
    if (due >= today) {
      return "No Due";
    }
    
    // Calculate days past due
    const diffTime = today.getTime() - due.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    // 0-30 Days Due
    if (diffDays >= 1 && diffDays <= 30) {
      return "0-30 Days Due";
    }
    
    // Overdue (>30 days)
    return "Overdue";
  };

  // Predicate for the invoices list. `except` lets us build cascading filter
  // options (each combobox lists values from the list filtered by all OTHER
  // active filters). `except` is a Set of filter keys to skip.
  const invoiceMatches = (invoice, except = new Set()) => {
    const projectName = getProjectName(invoice.project_id).toLowerCase();
    const clientName = getClientName(invoice.project_id).toLowerCase();
    const projectRef = (invoice.project_reference_invoice_number || "").toLowerCase();
    const invNum = (invoice.invoice_number || "").toLowerCase();

    if (!except.has("invoice_number") && filters.invoice_number && !invNum.includes(filters.invoice_number.toLowerCase())) return false;
    if (!except.has("client_name") && filters.client_name && !clientName.includes(filters.client_name.toLowerCase())) return false;
    if (!except.has("project_name") && filters.project_name && !projectName.includes(filters.project_name.toLowerCase())) return false;
    if (!except.has("project_reference_invoice_number") && filters.project_reference_invoice_number && !projectRef.includes(filters.project_reference_invoice_number.toLowerCase())) return false;

    if (!except.has("aging") && filters.aging.length > 0) {
      const excludeFromAging = ["paid", "paid_with_partial_writeoff", "writeoff"];
      if (excludeFromAging.includes(invoice.payment_status)) return false;
      const agingStatus = getAgingStatus(invoice.due_date);
      const matchesAnyAging = filters.aging.some(selectedAging => {
        if (selectedAging === "no_due") return agingStatus === "No Due";
        if (selectedAging === "due") return agingStatus === "0-30 Days Due";
        if (selectedAging === "overdue") return agingStatus === "Overdue";
        return false;
      });
      if (!matchesAnyAging) return false;
    }
    return true;
  };

  const filteredInvoices = invoices.filter(inv => invoiceMatches(inv));
  const paged = usePagedList(filteredInvoices);

  // Cascading option lists — computed against the invoice list filtered by
  // every OTHER active filter (so switching Aging trims Client/Project
  // choices and vice-versa).
  const invoicesForClientOptions = invoices.filter(inv => invoiceMatches(inv, new Set(["client_name"])));
  const invoicesForProjectOptions = invoices.filter(inv => invoiceMatches(inv, new Set(["project_name"])));

  const invClientOptions = Array.from(new Set(
    invoicesForClientOptions.map(inv => getClientName(inv.project_id)).filter(Boolean)
  )).sort().map(name => ({ value: name, label: name }));

  const invProjectOptions = Array.from(
    invoicesForProjectOptions.reduce((m, inv) => {
      if (!m.has(inv.project_id)) {
        const p = projects.find(pr => pr.id === inv.project_id);
        if (p) m.set(inv.project_id, p);
      }
      return m;
    }, new Map()).values()
  )
    .sort((a, b) => (a.project_number || "").localeCompare(b.project_number || ""))
    .map(p => ({ value: p.name || "", label: p.name || "", sublabel: p.project_number || "" }));

  const clearFilters = () => {
    setFilters({
      invoice_number: "",
      client_name: "",
      project_name: "",
      project_reference_invoice_number: "",
      aging: []
    });
  };

  return (
    <Layout user={user}>
      <div className="space-y-6" data-testid="invoices-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-md3-display-small text-md3-on-surface mb-2">Invoices</h1>
            <p className="text-md3-on-surface-variant">Manage invoicing and collections</p>
          </div>
          <div className="flex gap-2">
            <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
            <DialogTrigger asChild>
              <Button data-testid="create-invoice-button" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full">
                <Plus className="mr-2 h-4 w-4" /> New Invoice
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
              <DialogHeader>
                <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">{editMode ? 'Edit Invoice' : 'Create New Invoice'}</DialogTitle>
                <DialogDescription className="text-md3-body-medium text-gray-600">
                  {editMode ? 'Update invoice details' : 'Generate an invoice for a project'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="project" className="text-md3-label-large text-gray-700 font-medium">Project *</Label>
                  <Select value={formData.project_id} onValueChange={handleProjectChange} disabled={editMode}>
                    <SelectTrigger data-testid="invoice-project-select">
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map((project) => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.project_number ? `${project.project_number} - ${project.name}` : project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {projectBalance && (
                  <div className="space-y-3 col-span-2 p-4 bg-blue-50 rounded-md3-lg border border-blue-200">
                    <h4 className="font-semibold text-blue-900">Invoice Balance</h4>
                    
                    {/* Grid Breakdown Table */}
                    {projectBalance.breakdown && (
                      <div className="overflow-x-auto">
                        <table className="w-full text-md3-body-small border-collapse">
                          <thead>
                            <tr className="bg-blue-100">
                              <th className="border border-blue-300 p-2 text-left font-semibold">Type</th>
                              <th className="border border-blue-300 p-2 text-right font-semibold">Base</th>
                              <th className="border border-blue-300 p-2 text-right font-semibold">CO</th>
                              <th className="border border-blue-300 p-2 text-right font-semibold">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="bg-white">
                              <td className="border border-blue-200 p-2 font-medium">Detailing</td>
                              <td className="border border-blue-200 p-2 text-right">${projectBalance.breakdown.base_detailing.toLocaleString()}</td>
                              <td className="border border-blue-200 p-2 text-right">${projectBalance.breakdown.co_detailing.toLocaleString()}</td>
                              <td className="border border-blue-200 p-2 text-right font-semibold">${projectBalance.breakdown.total_detailing.toLocaleString()}</td>
                            </tr>
                            <tr className="bg-white">
                              <td className="border border-blue-200 p-2 font-medium">Engineering</td>
                              <td className="border border-blue-200 p-2 text-right">${projectBalance.breakdown.base_engineering.toLocaleString()}</td>
                              <td className="border border-blue-200 p-2 text-right">${projectBalance.breakdown.co_engineering.toLocaleString()}</td>
                              <td className="border border-blue-200 p-2 text-right font-semibold">${projectBalance.breakdown.total_engineering.toLocaleString()}</td>
                            </tr>
                            <tr className="bg-blue-100 font-semibold">
                              <td className="border border-blue-300 p-2">Total</td>
                              <td className="border border-blue-300 p-2 text-right">${projectBalance.base_price.toLocaleString()}</td>
                              <td className="border border-blue-300 p-2 text-right">${projectBalance.approved_change_orders.toLocaleString()}</td>
                              <td className="border border-blue-300 p-2 text-right">${projectBalance.total_project_value.toLocaleString()}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                    
                    {/* Summary */}
                    <div className="grid grid-cols-2 gap-2 text-md3-body-small mt-3 pt-3 border-t border-blue-300">
                      <div className="text-md3-on-surface-variant">Already Invoiced:</div>
                      <div className="font-medium text-orange-600 text-right">${projectBalance.total_invoiced.toLocaleString()}</div>
                      <div className="text-blue-900 font-semibold">Balance to Invoice:</div>
                      <div className="font-bold text-green-600 text-lg text-right">${projectBalance.balance_to_invoice.toLocaleString()}</div>
                    </div>

                    {/* Project Can Invoice Balance (Only Detailing) */}
                    {projectBalance.breakdown && projectBalance.project_completion_percentage !== undefined && (
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                        <div className="grid grid-cols-2 gap-2 text-md3-body-small">
                          <div className="text-blue-900 font-semibold">Project can Invoice Balance:</div>
                          <div className="font-bold text-blue-700 text-right">
                            ${((((projectBalance.project_completion_percentage / 100) * projectBalance.breakdown.total_detailing) - projectBalance.total_invoiced).toFixed(2)).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                          </div>
                        </div>
                        <p className="text-xs text-blue-600 mt-1 italic">
                          (Only Detailing: ({projectBalance.project_completion_percentage.toFixed(1)}% × ${projectBalance.breakdown.total_detailing.toLocaleString()}) - ${projectBalance.total_invoiced.toLocaleString()} Already Invoiced)
                        </p>
                      </div>
                    )}

                    {projectBalance.last_project_reference_invoice_number && (
                      <p className="text-xs text-md3-on-surface-variant">
                        Last project ref#: {projectBalance.last_project_reference_invoice_number}
                      </p>
                    )}

                    {/* Collapsible Invoice History */}
                    {projectBalance.invoice_history && projectBalance.invoice_history.length > 0 && (
                      <details className="mt-3">
                        <summary className="cursor-pointer text-md3-body-small font-semibold text-blue-900 hover:text-blue-700">
                          Invoice History ({projectBalance.invoice_history.length})
                        </summary>
                        <div className="mt-2 space-y-1">
                          {projectBalance.invoice_history.map((inv, idx) => (
                            <div key={idx} className="text-xs bg-md3-surface-container p-2 rounded border border-blue-200 flex justify-between items-center">
                              <div>
                                <span className="font-medium">{inv.project_reference_invoice_number || inv.invoice_number}</span>
                                {inv.issue_date && (
                                  <span className="text-md3-on-surface-variant ml-2">
                                    ({new Date(inv.issue_date).toLocaleDateString()})
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="font-semibold">${inv.amount.toLocaleString()}</span>
                                <Badge 
                                  variant={inv.payment_status === 'paid' ? 'default' : 'outline'}
                                  className="text-xs"
                                >
                                  {inv.payment_status}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="invoice_number" className="text-md3-label-large text-gray-700 font-medium">Invoice Number</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="invoice_number"
                    data-testid="invoice-number-input"
                    placeholder="e.g., INV-00001 (Leave empty for auto)"
                    value={formData.invoice_number}
                    onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                  />
                  <p className="text-xs text-md3-on-surface-variant">Leave empty to auto-generate</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="project_reference_invoice_number" className="text-md3-label-large text-gray-700 font-medium">Project Reference Invoice #</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="project_reference_invoice_number"
                    data-testid="project-ref-invoice-input"
                    placeholder="e.g., PR-001"
                    value={formData.project_reference_invoice_number}
                    onChange={(e) => setFormData({ ...formData, project_reference_invoice_number: e.target.value })}
                  />
                  <p className="text-xs text-md3-on-surface-variant">Project-specific invoice reference</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bill_to_name" className="text-md3-label-large text-gray-700 font-medium">Bill To Name</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="bill_to_name"
                    data-testid="invoice-bill-to-name-input"
                    placeholder="Recipient name (auto-populated from billing contact)"
                    value={formData.bill_to_name || ''}
                    onChange={(e) => setFormData({ ...formData, bill_to_name: e.target.value })}
                  />
                  <p className="text-xs text-md3-on-surface-variant">Leave empty to use billing contact from client</p>
                </div>

                {/* Line Items Section */}
                <div className="col-span-2 space-y-3 border rounded-md3-lg p-4 bg-md3-surface">
                  {canViewCosts() ? (
                    <>
                      <div className="flex justify-between items-center">
                        <Label className="text-md3-body-medium font-semibold ">Invoice Line Items *</Label>
                        <Button type="button" size="sm" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={addLineItem}>
                          <Plus className="mr-2 h-3 w-3" /> Add Line Item
                        </Button>
                      </div>
                      <div className="space-y-2">
                        {formData.invoice_items.map((item, index) => (
                          <div key={index} className="flex gap-2 items-start bg-md3-surface-container p-3 rounded border">
                            <div className="flex-1">
                              <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" placeholder="Description (e.g., Design Work, Development)"
                                value={item.description}
                                onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                                required
                              />
                            </div>
                            <div className="w-40">
                              <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" type="number"
                                step="0.01"
                                placeholder="Amount"
                                value={item.amount}
                                onChange={(e) => updateLineItem(index, 'amount', e.target.value)}
                                required
                              />
                            </div>
                            <Button
                              type="button"
                              size="sm"
                              variant="destructive"
                              className="bg-red-600 hover:bg-red-700 text-white shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"
                              onClick={() => removeLineItem(index)}
                              disabled={formData.invoice_items.length === 1}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-end items-center pt-2 border-t">
                        <div className="text-right">
                          <p className="text-md3-body-small text-md3-on-surface-variant">Total Amount:</p>
                          <p className="text-md3-headline-large text-green-600">${calculateTotal().toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                          {projectBalance && calculateTotal() > projectBalance.balance_to_invoice && (
                            <p className="text-xs text-red-600 font-medium">
                              Exceeds balance to invoice!
                            </p>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="p-6 bg-md3-surface rounded-md3-lg text-center">
                      <p className="text-md3-on-surface-variant">Invoice creation and financial information is restricted to Management, Sales, and Accounts departments.</p>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="issue_date" className="text-md3-label-large text-gray-700 font-medium">Issue Date *</Label>
                  <DatePicker
                    id="issue_date"
                    data-testid="invoice-issue-date-input"
                    value={formData.issue_date}
                    onChange={async (e) => {
                      const newIssueDate = e.target.value;
                      setFormData({ ...formData, issue_date: newIssueDate });
                      
                      // Auto-calculate due date when issue date changes
                      if (formData.project_id && newIssueDate) {
                        try {
                          const project = projects.find(p => p.id === formData.project_id);
                          if (project && project.client_id) {
                            const clientResponse = await api.get(`/clients/${project.client_id}`);
                            const client = clientResponse.data;
                            const billingCycleDays = client.billing_cycle_days || 30;
                            const newDueDate = calculateDueDate(newIssueDate, billingCycleDays);
                            if (newDueDate) {
                              setFormData(prev => ({ ...prev, issue_date: newIssueDate, due_date: newDueDate }));
                            }
                          }
                        } catch (error) {
                          console.error("Failed to calculate due date");
                        }
                      }
                    }}
                    required
                  />
                  <p className="text-xs text-md3-on-surface-variant">Due date will be auto-calculated based on billing cycle</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="due_date" className="text-md3-label-large text-gray-700 font-medium">Due Date *</Label>
                  <DatePicker
                    id="due_date"
                    data-testid="invoice-due-date-input"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    required
                  />
                  <p className="text-xs text-md3-on-surface-variant">Editable - auto-calculated from issue date + billing cycle days</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes" className="text-md3-label-large text-gray-700 font-medium">Notes</Label>
                  <Textarea className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="notes"
                    data-testid="invoice-notes-input"
                    placeholder="Additional notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  />
                </div>
                <Button type="submit" className="w-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" data-testid="invoice-submit-button"  >
                  {editMode ? 'Update Invoice' : 'Create Invoice'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {canViewCosts() && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-blue-50/50 border border-blue-200 shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg">
              <CardHeader>
                <CardTitle className="text-md3-body-small font-medium text-md3-on-surface-variant ">Total Revenue (USD)</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-md3-display-small">${getTotalRevenue().toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</p>
                {(() => {
                  const b = getRevenueBreakdown();
                  return (b.cadTotal > 0 || b.usdTotal > 0) && (
                    <div className="text-xs text-gray-500 mt-2 space-y-0.5">
                      {b.usdTotal > 0 && <div>USD: ${b.usdTotal.toLocaleString()}</div>}
                      {b.cadTotal > 0 && <div>CAD: ${b.cadTotal.toLocaleString()} (≈ ${b.cadToUsdTotal.toLocaleString()} USD)</div>}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
            <Card className="bg-green-50/50 border border-green-200 shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg">
              <CardHeader>
                <CardTitle className="text-md3-body-small font-medium text-md3-on-surface-variant ">Collected (USD)</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-md3-display-small text-green-600">${getCollectedRevenue().toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</p>
                {(() => {
                  const b = getRevenueBreakdown();
                  return (b.cadCollected > 0 || b.usdCollected > 0) && (
                    <div className="text-xs text-gray-500 mt-2 space-y-0.5">
                      {b.usdCollected > 0 && <div>USD: ${b.usdCollected.toLocaleString()}</div>}
                      {b.cadCollected > 0 && <div>CAD: ${b.cadCollected.toLocaleString()} (≈ ${b.cadToUsdCollected.toLocaleString()} USD)</div>}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
            <Card className="bg-amber-50/50 border border-amber-200 shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg">
              <CardHeader>
                <CardTitle className="text-md3-body-small font-medium text-md3-on-surface-variant ">Pending (USD)</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-md3-display-small text-orange-600">${getPendingRevenue().toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</p>
                {(() => {
                  const b = getRevenueBreakdown();
                  return (b.cadBalance > 0 || b.usdBalance > 0) && (
                    <div className="text-xs text-gray-500 mt-2 space-y-0.5">
                      {b.usdBalance > 0 && <div>USD: ${b.usdBalance.toLocaleString()}</div>}
                      {b.cadBalance > 0 && <div>CAD: ${b.cadBalance.toLocaleString()} (≈ ${b.cadToUsdBalance.toLocaleString()} USD)</div>}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </div>
        )}

        <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <CardHeader className="pb-2">
              <TabsList data-testid="inv-tabs">
                <TabsTrigger value="all" data-testid="inv-tab-all">All Invoices</TabsTrigger>
                <TabsTrigger value="project-invoice" data-testid="inv-tab-project">Project-Invoice</TabsTrigger>
                <TabsTrigger value="ready-to-invoice" data-testid="inv-tab-ready">Ready to Invoice</TabsTrigger>
              </TabsList>
            </CardHeader>
            <TabsContent value="all" className="m-0">
              <CardContent>
            {/* Filter Section */}
            {!loading && invoices.length > 0 && (
              <div className="mb-6 p-4 bg-md3-surface rounded-md3-lg border border-md3-outline-variant">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-md3-body-small font-semibold text-md3-on-surface-variant">Filters</h3>
                  <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" size="sm"  onClick={clearFilters}>Clear Filters</Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="filter_invoice_num" className="text-xs">Invoice #</Label>
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="filter_invoice_num"
                      placeholder="Search by invoice #..."
                      value={filters.invoice_number}
                      onChange={(e) => setFilters({ ...filters, invoice_number: e.target.value })}
                      data-testid="inv-filter-invoice-number"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="filter_client" className="text-xs">Client Name</Label>
                    <Popover open={clientPickerOpen} onOpenChange={setClientPickerOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={clientPickerOpen}
                          className="w-full justify-between font-normal bg-white border-gray-300"
                          data-testid="inv-filter-client"
                        >
                          <span className={filters.client_name ? "" : "text-md3-on-surface-variant"}>
                            {filters.client_name || "All clients"}
                          </span>
                          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                        <Command>
                          <CommandInput placeholder="Type to search…" />
                          <CommandList>
                            <CommandEmpty>No client matches.</CommandEmpty>
                            <CommandGroup>
                              <CommandItem
                                value="__all"
                                onSelect={() => { setFilters({ ...filters, client_name: "" }); setClientPickerOpen(false); }}
                              >
                                <Check className={`mr-2 h-4 w-4 ${!filters.client_name ? "opacity-100" : "opacity-0"}`} />
                                All clients
                              </CommandItem>
                              {invClientOptions.map(opt => (
                                <CommandItem
                                  key={opt.value}
                                  value={opt.value}
                                  onSelect={() => { setFilters({ ...filters, client_name: opt.value }); setClientPickerOpen(false); }}
                                  className="whitespace-nowrap"
                                >
                                  <Check className={`mr-2 h-4 w-4 shrink-0 ${filters.client_name === opt.value ? "opacity-100" : "opacity-0"}`} />
                                  <span className="truncate">{opt.label}</span>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="filter_project" className="text-xs">Project Name</Label>
                    <Popover open={projectPickerOpen} onOpenChange={setProjectPickerOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={projectPickerOpen}
                          className="w-full justify-between font-normal bg-white border-gray-300"
                          data-testid="inv-filter-project"
                        >
                          <span className={filters.project_name ? "" : "text-md3-on-surface-variant"}>
                            {filters.project_name || "All projects"}
                          </span>
                          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                        <Command>
                          <CommandInput placeholder="Type to search…" />
                          <CommandList>
                            <CommandEmpty>No project matches.</CommandEmpty>
                            <CommandGroup>
                              <CommandItem
                                value="__all"
                                onSelect={() => { setFilters({ ...filters, project_name: "" }); setProjectPickerOpen(false); }}
                              >
                                <Check className={`mr-2 h-4 w-4 ${!filters.project_name ? "opacity-100" : "opacity-0"}`} />
                                All projects
                              </CommandItem>
                              {invProjectOptions.map(opt => (
                                <CommandItem
                                  key={opt.value + opt.sublabel}
                                  value={opt.sublabel + " " + opt.label}
                                  onSelect={() => { setFilters({ ...filters, project_name: opt.value }); setProjectPickerOpen(false); }}
                                  className="whitespace-nowrap"
                                >
                                  <Check className={`mr-2 h-4 w-4 shrink-0 ${filters.project_name === opt.value ? "opacity-100" : "opacity-0"}`} />
                                  <span className="font-mono text-xs text-md3-primary mr-2 shrink-0">{opt.sublabel}</span>
                                  <span className="truncate">{opt.label}</span>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="filter_project_ref" className="text-xs">Project Reference Invoice #</Label>
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="filter_project_ref"
                      data-testid="inv-filter-project-ref"
                      placeholder="Search by ref #..."
                      value={filters.project_reference_invoice_number}
                      onChange={(e) => setFilters({ ...filters, project_reference_invoice_number: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-md3-label-large text-gray-700 font-medium">Aging (Multiple Selection)</Label>
                    <div className="border rounded-md3-md p-3 space-y-2 bg-white">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="aging_no_due"
                          checked={filters.aging.includes("no_due")}
                          onChange={(e) => {
                            const newAging = e.target.checked 
                              ? [...filters.aging, "no_due"]
                              : filters.aging.filter(a => a !== "no_due");
                            setFilters({ ...filters, aging: newAging });
                          }}
                          className="h-4 w-4"
                        />
                        <label htmlFor="aging_no_due" className="text-md3-body-small cursor-pointer">No Due</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="aging_due"
                          checked={filters.aging.includes("due")}
                          onChange={(e) => {
                            const newAging = e.target.checked 
                              ? [...filters.aging, "due"]
                              : filters.aging.filter(a => a !== "due");
                            setFilters({ ...filters, aging: newAging });
                          }}
                          className="h-4 w-4"
                        />
                        <label htmlFor="aging_due" className="text-md3-body-small cursor-pointer">0-30 Days Due</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="aging_overdue"
                          checked={filters.aging.includes("overdue")}
                          onChange={(e) => {
                            const newAging = e.target.checked 
                              ? [...filters.aging, "overdue"]
                              : filters.aging.filter(a => a !== "overdue");
                            setFilters({ ...filters, aging: newAging });
                          }}
                          className="h-4 w-4"
                        />
                        <label htmlFor="aging_overdue" className="text-md3-body-small cursor-pointer">Overdue (&gt;30 Days)</label>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" size="sm"  onClick={() => setReportDialogOpen(true)}>
                    <FileText className="mr-2 h-4 w-4" /> View Report
                  </Button>
                </div>
              </div>
            )}

            {loading ? (
              <p className="text-md3-on-surface-variant">Loading...</p>
            ) : invoices.length === 0 ? (
              <p className="text-md3-on-surface-variant">No invoices yet</p>
            ) : filteredInvoices.length === 0 ? (
              <p className="text-md3-on-surface-variant">No invoices match the filters</p>
            ) : (
              <>
                <div className="mb-4 text-md3-body-small text-md3-on-surface-variant">
                  Showing {filteredInvoices.length} of {invoices.length} invoices
                </div>
                <div className="overflow-x-auto">
                <Table className="bg-md3-surface-container rounded-md3-md">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>Project Ref #</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Project</TableHead>
                      {canViewCosts() && <TableHead>Amount</TableHead>}
                      {canViewCosts() && <TableHead>Currency</TableHead>}
                      <TableHead>Issue Date</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead>Last Reminder</TableHead>
                      <TableHead>Aging</TableHead>
                      <TableHead>Status</TableHead>
                      {canViewCosts() && <TableHead>Paid</TableHead>}
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paged.pageItems.map((invoice) => (
                      <TableRow key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                        <TableCell className="font-medium">{invoice.invoice_number}</TableCell>
                        <TableCell className="text-md3-body-small text-md3-on-surface-variant">{invoice.project_reference_invoice_number || '-'}</TableCell>
                        <TableCell className="text-md3-body-small">{getClientName(invoice.project_id)}</TableCell>
                        <TableCell>{getProjectName(invoice.project_id)}</TableCell>
                        {canViewCosts() && (
                          <TableCell className="font-semibold">
                            {formatAmountWithCurrency(invoice.amount, getInvoiceCurrency(invoice))}
                            {getInvoiceCurrency(invoice) === "CAD" && invoice.usd_amount && (
                              <div className="text-xs text-gray-500">≈ {invoice.usd_amount.toLocaleString()} USD</div>
                            )}
                          </TableCell>
                        )}
                        {canViewCosts() && (
                          <TableCell>
                            <Badge variant={getInvoiceCurrency(invoice) === "CAD" ? "secondary" : "default"}>
                              {getInvoiceCurrency(invoice)}
                            </Badge>
                          </TableCell>
                        )}
                        <TableCell>{format(new Date(invoice.issue_date), "MMM dd, yyyy")}</TableCell>
                        <TableCell>{format(new Date(invoice.due_date), "MMM dd, yyyy")}</TableCell>
                        <TableCell>
                          {getLastReminderDate(invoice) ? (
                            <span className="text-md3-body-small">{format(new Date(getLastReminderDate(invoice)), "MMM dd, yyyy")}</span>
                          ) : (
                            <span className="text-md3-body-small text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {(invoice.payment_status === "paid" || invoice.payment_status === "paid_with_partial_writeoff" || invoice.payment_status === "writeoff") ? (
                            <span className="text-md3-body-small text-md3-on-surface-variant">-</span>
                          ) : (
                            <Badge variant={
                              getAgingStatus(invoice.due_date) === "No Due" ? "default" :
                              getAgingStatus(invoice.due_date) === "0-30 Days Due" ? "secondary" : "destructive"
                            }>
                              {getAgingStatus(invoice.due_date)}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(invoice.payment_status)}>{formatPaymentStatus(invoice.payment_status)}</Badge>
                        </TableCell>
                        {canViewCosts() && (
                          <TableCell className="text-green-600 font-semibold">
                            {formatAmountWithCurrency(invoice.amount_paid, getInvoiceCurrency(invoice))}
                            {getInvoiceCurrency(invoice) === "CAD" && invoice.usd_amount_paid && (
                              <div className="text-xs text-gray-500">≈ {invoice.usd_amount_paid.toLocaleString()} USD</div>
                            )}
                          </TableCell>
                        )}
                        <TableCell>
                          <div className="flex gap-2 flex-wrap">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
                              onClick={() => navigate(`/invoices/${invoice.id}`)}
                              title="View Details"
                            >
                              View Details
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="hover:bg-md3-surface-container-high rounded-md3-full transition-all duration-200"
                              onClick={() => handleViewInvoice(invoice)}
                              title="View Invoice"
                              data-testid={`view-invoice-${invoice.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
                              onClick={() => openPaymentDialog(invoice)}
                              data-testid={`update-payment-${invoice.id}`}
                            >
                              Update Payment
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
                              onClick={() => handleEdit(invoice)}
                              data-testid={`edit-invoice-${invoice.id}`}
                            >
                              Edit
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Pagination
                  currentPage={paged.page}
                  totalItems={paged.total}
                  pageSize={paged.pageSize}
                  onPageChange={paged.onPageChange}
                  onPageSizeChange={paged.onPageSizeChange}
                  pageSizeOptions={[25, 50, 100]}
                  testIdPrefix="invoices-pagination"
                />
              </div>
              </>
            )}
          </CardContent>
            </TabsContent>

            {/* ─────── Project-Invoice Tab ─────── */}
            <TabsContent value="project-invoice" className="m-0">
              <CardContent>
                <ProjectInvoiceTab
                  onOpenNewInvoice={(prefill) => {
                    setFormData(f => ({ ...f, ...prefill }));
                    setDialogOpen(true);
                  }}
                />
              </CardContent>
            </TabsContent>

            {/* ─────── Ready to Invoice Tab ─────── */}
            <TabsContent value="ready-to-invoice" className="m-0">
              <CardContent>
                <ReadyToInvoiceTab
                  refreshKey={rtiBump}
                  onGenerate={async (prefill) => {
                    // Reproduce the exact "New Invoice" flow so the Invoice Balance
                    // panel, billing contact, and due-date all populate correctly.
                    if (prefill.project_id) {
                      await handleProjectChange(prefill.project_id);
                    }
                    // Then override line items + notes with the proposal data.
                    setFormData(f => ({
                      ...f,
                      invoice_items: prefill.invoice_items || f.invoice_items,
                      notes: prefill.notes ?? f.notes,
                    }));
                    // Remember this proposal so we can auto-delete it after save.
                    setLinkedProposalId(prefill.proposal_id || null);
                    setDialogOpen(true);
                  }}
                  onInvoiced={() => fetchInvoices()}
                />
              </CardContent>
            </TabsContent>
          </Tabs>
        </Card>

        <Dialog open={paymentDialogOpen} onOpenChange={(open) => {
          setPaymentDialogOpen(open);
          if (!open) resetPaymentForm();
        }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Manage Payments</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                Invoice: {selectedInvoice?.invoice_number} - Invoice Amount: ${selectedInvoice?.amount?.toLocaleString()}
              </DialogDescription>
            </DialogHeader>
            
            {selectedInvoice && (
              <div className="space-y-6">
                {/* Payment Progress */}
                <div className="bg-md3-surface p-4 rounded-md3-lg space-y-2">
                  <div className="flex justify-between text-md3-body-small">
                    <span className="font-semibold">Payment Progress</span>
                    <span className="font-semibold">${selectedInvoice.amount_paid?.toLocaleString()} / ${selectedInvoice.amount?.toLocaleString()}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className="bg-green-600 h-3 rounded-full transition-all" 
                      style={{ width: `${Math.min((selectedInvoice.amount_paid / selectedInvoice.amount) * 100, 100)}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-md3-on-surface-variant">
                    <span>Remaining: ${(selectedInvoice.amount - selectedInvoice.amount_paid).toLocaleString()}</span>
                    <span><Badge className={getStatusColor(selectedInvoice.payment_status)}>{formatPaymentStatus(selectedInvoice.payment_status)}</Badge></span>
                  </div>
                </div>

                {/* Payment History */}
                {selectedInvoice.payment_entries && selectedInvoice.payment_entries.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-3">Payment History</h4>
                    <div className="border rounded-md3-lg overflow-hidden">
                      <Table className="bg-md3-surface-container rounded-md3-md">
                        <TableHeader>
                          <TableRow>
                            <TableHead>Amount</TableHead>
                            <TableHead>Mode</TableHead>
                            <TableHead>Reference</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedInvoice.payment_entries.map((payment) => (
                            <TableRow key={payment.id} className={editingPaymentId === payment.id ? "bg-blue-50" : ""}>
                              <TableCell className="font-semibold">${payment.amount?.toLocaleString()}</TableCell>
                              <TableCell>{payment.payment_mode || "-"}</TableCell>
                              <TableCell>{payment.payment_reference || "-"}</TableCell>
                              <TableCell>{payment.date_received ? format(new Date(payment.date_received), "MMM dd, yyyy") : "-"}</TableCell>
                              <TableCell><Badge variant="outline">{payment.payment_workflow_status}</Badge></TableCell>
                              <TableCell>
                                <div className="flex gap-2">
                                  <Button type="button"
                                    size="sm"
                                    variant="outline"
                                    data-testid={`payment-view-btn-${payment.id}`}
                                    className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
                                    onClick={() => setViewingPayment(payment)}>
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  <Button type="button"
                                    size="sm" 
                                    variant="outline"
                                    className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
                                    onClick={() => startEditingPayment(payment)}>
                                    Edit
                                  </Button>
                                  <Button type="button"
                                    size="sm" 
                                    variant="destructive"
                                    className="bg-red-600 hover:bg-red-700 text-white shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"
                                    onClick={() => handleDeletePayment(payment.id)}>
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Payment Form */}
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold">
                      {paymentFormMode === "add" ? "Add New Payment" : "Edit Payment"}
                    </h4>
                    {paymentFormMode === "edit" && (
                      <Badge variant="outline" className="bg-blue-50">Editing</Badge>
                    )}
                  </div>
                  <form onSubmit={paymentFormMode === "add" ? handleAddPayment : handleEditPayment} className="space-y-6" data-payment-form>
                    <div className="space-y-2">
                      <Label htmlFor="amount" className="text-md3-label-large text-gray-700 font-medium">Amount ($) *</Label>
                      <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="amount"
                        type="number"
                        step="0.01"
                        placeholder="Payment amount"
                        value={paymentData.amount}
                        onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                        required
                      />
                      <p className="text-xs text-md3-on-surface-variant">
                        Maximum allowed: ${(selectedInvoice.amount - selectedInvoice.amount_paid + (paymentFormMode === "edit" && editingPaymentId ? (selectedInvoice.payment_entries?.find(p => p.id === editingPaymentId)?.amount || 0) : 0)).toLocaleString()}
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="payment_mode" className="text-md3-label-large text-gray-700 font-medium">Mode of Payment</Label>
                        <Select value={paymentData.payment_mode} onValueChange={(value) => setPaymentData({ ...paymentData, payment_mode: value })}>
                          <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                            <SelectValue placeholder="Select mode" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Cheque">Cheque</SelectItem>
                            <SelectItem value="Wire transfer">Wire transfer</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="bank_name" className="text-md3-label-large text-gray-700 font-medium">Bank Name</Label>
                        <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="bank_name"
                          type="text"
                          placeholder="Enter bank name"
                          value={paymentData.bank_name}
                          onChange={(e) => setPaymentData({ ...paymentData, bank_name: e.target.value })}
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="payment_reference" className="text-md3-label-large text-gray-700 font-medium">Reference/Tracking #</Label>
                      <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="payment_reference"
                        type="text"
                        placeholder="Optional"
                        value={paymentData.payment_reference}
                        onChange={(e) => setPaymentData({ ...paymentData, payment_reference: e.target.value })}
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="date_received" className="text-md3-label-large text-gray-700 font-medium">Date Received</Label>
                        <DatePicker
                          id="date_received"
                          value={paymentData.date_received}
                          onChange={(e) => setPaymentData({ ...paymentData, date_received: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="payment_workflow_status" className="text-md3-label-large text-gray-700 font-medium">Workflow Status *</Label>
                        <Select value={paymentData.payment_workflow_status} onValueChange={(value) => setPaymentData({ ...paymentData, payment_workflow_status: value })}>
                          <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Received">Received</SelectItem>
                            <SelectItem value="Deposited">Deposited</SelectItem>
                            <SelectItem value="Cleared">Cleared</SelectItem>
                            <SelectItem value="Write-off">Write-off</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    {/* Conditional Date Fields */}
                    {paymentData.payment_mode === "Cheque" && (paymentData.payment_workflow_status === "Deposited" || paymentData.payment_workflow_status === "Cleared") && (
                      <div className="space-y-2">
                        <Label htmlFor="date_deposited" className="text-md3-label-large text-gray-700 font-medium">Date Deposited</Label>
                        <DatePicker
                          id="date_deposited"
                          value={paymentData.date_deposited}
                          onChange={(e) => setPaymentData({ ...paymentData, date_deposited: e.target.value })}
                        />
                        <p className="text-xs text-md3-on-surface-variant">Date when cheque was deposited</p>
                      </div>
                    )}
                    
                    {paymentData.payment_workflow_status === "Cleared" && (
                      <div className="space-y-2">
                        <Label htmlFor="date_cleared" className="text-md3-label-large text-gray-700 font-medium">Payment Cleared Date</Label>
                        <DatePicker
                          id="date_cleared"
                          value={paymentData.date_cleared}
                          onChange={(e) => setPaymentData({ ...paymentData, date_cleared: e.target.value })}
                        />
                        <p className="text-xs text-md3-on-surface-variant">Required when status is Cleared (for all payment modes)</p>
                      </div>
                    )}
                    
                    {paymentData.payment_workflow_status === "Write-off" && (
                      <div className="space-y-2">
                        <Label htmlFor="writeoff_reason" className="text-md3-label-large text-gray-700 font-medium">Write-off Reason *</Label>
                        <Textarea className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="writeoff_reason"
                          placeholder="Enter reason for write-off..."
                          rows={3}
                          value={paymentData.writeoff_reason}
                          onChange={(e) => setPaymentData({ ...paymentData, writeoff_reason: e.target.value })}
                          required
                        />
                        <p className="text-xs text-md3-on-surface-variant">Required when status is Write-off</p>
                      </div>
                    )}
                    
                    <div className="flex gap-2">
                      <Button type="submit" className="flex-1 bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" >
                        {paymentFormMode === "add" ? "Add Payment" : "Update Payment"}
                      </Button>
                      {paymentFormMode === "edit" && (
                        <Button type="button" variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" onClick={resetPaymentForm}>
                          Cancel
                        </Button>
                      )}
                    </div>
                  </form>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Payment Details Dialog */}
        <Dialog open={!!viewingPayment} onOpenChange={(o) => { if (!o) setViewingPayment(null); }}>
          <DialogContent className="max-w-2xl bg-white" data-testid="payment-details-dialog">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold">Payment Details</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                Full record for this payment entry
              </DialogDescription>
            </DialogHeader>
            {viewingPayment && (() => {
              const p = viewingPayment;
              const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
              const fmtDate = (d) => d ? format(new Date(d), "MMM dd, yyyy") : "—";
              const fmtDateTime = (d) => d ? format(new Date(d), "MMM dd, yyyy · hh:mm a") : "—";
              const row = (label, value) => (
                <div className="grid grid-cols-3 gap-3 py-2 border-b last:border-b-0" key={label}>
                  <span className="text-sm text-md3-on-surface-variant">{label}</span>
                  <span className="col-span-2 text-sm font-medium text-md3-on-surface">{value ?? "—"}</span>
                </div>
              );
              return (
                <div className="space-y-4">
                  <div className="rounded-md3-lg border bg-md3-surface-container p-4">
                    <p className="text-xs uppercase tracking-wide text-md3-on-surface-variant mb-1">Amount</p>
                    <p className="text-2xl font-bold text-green-600" data-testid="payment-details-amount">{money(p.amount)}</p>
                    <div className="mt-2 flex gap-2 items-center">
                      <Badge variant="outline" className="text-xs">{p.payment_workflow_status || "—"}</Badge>
                      {p.payment_mode && <Badge className="bg-md3-primary text-md3-on-primary text-xs">{p.payment_mode}</Badge>}
                    </div>
                  </div>

                  <div className="rounded-md3-lg border bg-white p-4">
                    <h4 className="font-semibold mb-2 text-md3-title-medium">Payment Instrument</h4>
                    {row("Mode of Payment", p.payment_mode || "—")}
                    {row("Bank Name", p.bank_name || "—")}
                    {row("Reference / Tracking #", p.payment_reference || "—")}
                  </div>

                  <div className="rounded-md3-lg border bg-white p-4">
                    <h4 className="font-semibold mb-2 text-md3-title-medium">Timeline</h4>
                    {row("Date Received", fmtDate(p.date_received))}
                    {row("Date Deposited", fmtDate(p.date_deposited))}
                    {row("Payment Cleared Date", fmtDate(p.date_cleared))}
                  </div>

                  <div className="rounded-md3-lg border bg-white p-4">
                    <h4 className="font-semibold mb-2 text-md3-title-medium">Workflow</h4>
                    {row("Workflow Status", p.payment_workflow_status || "—")}
                    {p.payment_workflow_status === "Write-off" && row("Write-off Reason", p.writeoff_reason || "—")}
                  </div>

                  {(p.created_at || p.updated_at || p.recorded_by || p.updated_by) && (
                    <div className="rounded-md3-lg border bg-white p-4">
                      <h4 className="font-semibold mb-2 text-md3-title-medium">Audit</h4>
                      {p.recorded_by && row("Recorded By", p.recorded_by)}
                      {p.created_at && row("Created At", fmtDateTime(p.created_at))}
                      {p.updated_by && row("Updated By", p.updated_by)}
                      {p.updated_at && row("Updated At", fmtDateTime(p.updated_at))}
                    </div>
                  )}

                  <div className="flex justify-end gap-2 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="border-md3-outline hover:bg-md3-surface-container-high rounded-md3-full"
                      onClick={() => setViewingPayment(null)}
                      data-testid="payment-details-close-btn"
                    >
                      Close
                    </Button>
                    <Button
                      type="button"
                      className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary rounded-md3-full"
                      onClick={() => { startEditingPayment(p); setViewingPayment(null); }}
                      data-testid="payment-details-edit-btn"
                    >
                      Edit Payment
                    </Button>
                  </div>
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>
        <Dialog open={viewInvoiceDialogOpen} onOpenChange={setViewInvoiceDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Invoice Preview</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                Invoice: {invoiceDetails?.invoice.project_reference_invoice_number || invoiceDetails?.invoice.invoice_number}
              </DialogDescription>
            </DialogHeader>
            
            {invoiceDetails && (() => {
              const cur = (invoiceDetails.project?.currency || "USD").toUpperCase();
              const curPrefix = cur === "CAD" ? "CA $" : "$";
              const money = (v) => `${(Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
              const items = (invoiceDetails.invoice.invoice_items && invoiceDetails.invoice.invoice_items.length > 0)
                ? invoiceDetails.invoice.invoice_items
                : [{ description: invoiceDetails.invoice.description || '-', amount: invoiceDetails.invoice.amount }];
              const clientCity = [invoiceDetails.client.billing_city, invoiceDetails.client.state, invoiceDetails.client.billing_zip_code].filter(Boolean).join(", ");
              const contactPhoneLine = `${invoiceDetails.billing_contact.phone || ''}${invoiceDetails.billing_contact.phone_ext ? ` Ext. ${invoiceDetails.billing_contact.phone_ext}` : ''}`.trim();
              const TEAL = "#6BA5B8";
              const TINT = "#EAF2F5";
              const ACCENT = "#2E7A9A";
              return (
                <div className="text-sm">
                  {/* Row 1: Teal header bar */}
                  <div className="flex items-center justify-between px-4 py-3" style={{ backgroundColor: TEAL }}>
                    <div className="text-white text-xl font-bold">Milekal Inc.</div>
                    <div className="text-white text-2xl font-bold tracking-wide">INVOICE</div>
                  </div>

                  {/* Row 2: Address (left) + Invoice info (right) on light tint */}
                  <div className="grid grid-cols-[3fr_2fr] gap-2 px-4 py-3" style={{ backgroundColor: TINT }}>
                    <div className="text-xs space-y-0.5" style={{ color: ACCENT }}>
                      <p>2093 Philadelphia Pike #1881</p>
                      <p>Claymont, DE 19703</p>
                      <p>USA</p>
                      <p>Tel:- +1-302-261-3174</p>
                      <p>www.milekal.com</p>
                    </div>
                    <div className="text-xs space-y-1">
                      <div className="grid grid-cols-[auto_1fr] gap-x-3">
                        <span>Invoice #</span>
                        <span className="font-bold text-right">{invoiceDetails.invoice.project_reference_invoice_number || invoiceDetails.invoice.invoice_number}</span>
                        <span>Date</span>
                        <span className="text-right">{format(new Date(invoiceDetails.invoice.issue_date), "dd-MMM-yy")}</span>
                        <span>Payment</span>
                        <span className="text-right">{formatPaymentStatus(invoiceDetails.invoice.payment_status)}</span>
                        <span>This Invoice</span>
                        <span className="text-right font-bold whitespace-nowrap">
                          <span className="font-bold mr-0.5" style={{ color: ACCENT }}>{curPrefix}</span>
                          {money(invoiceDetails.invoice.amount)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Row 3: Project info two-column, aligned with invoice info above */}
                  <div className="grid grid-cols-[3fr_2fr] gap-2 px-4 py-3 mt-3">
                    <div className="text-xs space-y-1">
                      <div className="grid grid-cols-[100px_1fr] gap-x-3">
                        <span>Project</span>
                        <span className="font-bold">{invoiceDetails.project.name || '-'}</span>
                        <span>Project #</span>
                        <span className="font-bold">{invoiceDetails.project.project_number || '-'}</span>
                      </div>
                    </div>
                    <div className="text-xs space-y-1">
                      <div className="grid grid-cols-[auto_1fr] gap-x-3">
                        <span>P.O #</span>
                        <span className="text-right font-bold">{invoiceDetails.project.purchase_order_number || '-'}</span>
                        <span>Project Award</span>
                        <span className="text-right font-bold">{invoiceDetails.project.start_date ? format(new Date(invoiceDetails.project.start_date), "dd-MMM-yy") : '-'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Row 4: Teal BILL TO / BILLING SUMMARY header bars + content */}
                  <div className="grid grid-cols-2 mt-3 text-white text-center font-semibold text-sm" style={{ backgroundColor: TEAL }}>
                    <div className="py-1.5 border-r border-white/40">BILL TO</div>
                    <div className="py-1.5">BILLING SUMMARY</div>
                  </div>
                  <div className="grid grid-cols-2 px-4 py-3 text-xs">
                    <div className="space-y-1">
                      <p>{invoiceDetails.invoice.bill_to_name || invoiceDetails.billing_contact.name || '-'}</p>
                      <p>{invoiceDetails.client.name}</p>
                      {invoiceDetails.client.billing_address_line1 && <p>{invoiceDetails.client.billing_address_line1}</p>}
                      {invoiceDetails.client.billing_address_line2 && <p>{invoiceDetails.client.billing_address_line2}</p>}
                      {clientCity && <p>{clientCity}</p>}
                      {invoiceDetails.client.country && <p>{invoiceDetails.client.country}</p>}
                      {contactPhoneLine && <p>Tel: {contactPhoneLine}</p>}
                    </div>
                    <div className="text-xs">
                      <div className="grid grid-cols-[auto_auto_1fr] gap-x-1 gap-y-1">
                        <span>Base Contract Value</span>
                        <span className="font-bold pl-3" style={{ color: ACCENT }}>{curPrefix}</span>
                        <span className="text-right">{money(invoiceDetails.calculations.base_contract_value)}</span>

                        <span>CCN Value</span>
                        <span className="font-bold pl-3" style={{ color: ACCENT }}>{curPrefix}</span>
                        <span className="text-right">{invoiceDetails.calculations.ccn_value > 0 ? money(invoiceDetails.calculations.ccn_value) : "-"}</span>

                        <span>Allowances Value</span>
                        <span className="font-bold pl-3" style={{ color: ACCENT }}>{curPrefix}</span>
                        <span className="text-right">-</span>

                        <span className="font-bold">Total Contract Value</span>
                        <span className="font-bold pl-3" style={{ color: ACCENT }}>{curPrefix}</span>
                        <span className="text-right font-bold">{money(invoiceDetails.calculations.total_contract_value)}</span>

                        <span className="font-bold">Invoiced till date (Includes this)</span>
                        <span className="font-bold pl-3" style={{ color: ACCENT }}>{curPrefix}</span>
                        <span className="text-right font-bold">{money(invoiceDetails.calculations.invoiced_till_date)}</span>

                        <span className="font-bold">Balance to Invoice</span>
                        <span className="font-bold pl-3" style={{ color: ACCENT }}>{curPrefix}</span>
                        <span className="text-right font-bold">{money(invoiceDetails.calculations.balance_to_invoice ?? (invoiceDetails.calculations.total_contract_value - invoiceDetails.calculations.invoiced_till_date))}</span>
                      </div>
                    </div>
                  </div>

                  {/* Row 5: Description items table */}
                  <div className="mt-3 border-t border-b border-gray-300">
                    <div className="grid grid-cols-[4fr_1fr_1fr] py-2 text-center font-semibold text-sm border-b border-gray-300" style={{ backgroundColor: TINT, color: ACCENT }}>
                      <div>Description</div>
                      <div>Unit Price ($)</div>
                      <div>Total ($)</div>
                    </div>
                    {items.map((item, index) => (
                      <div key={index} className="grid grid-cols-[4fr_1fr_1fr] py-2 px-3 text-xs border-b border-gray-200 last:border-b-0">
                        <div>{item.description}</div>
                        <div></div>
                        <div className="text-right whitespace-nowrap">
                          <span className="font-bold mr-0.5" style={{ color: ACCENT }}>{curPrefix}</span>{money(item.amount)}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* TOTAL row (bordered) */}
                  <div className="grid grid-cols-[4fr_1fr_1fr] py-2 px-3 border-b-2 border-gray-400 mt-0 text-base font-bold" style={{ backgroundColor: TINT }}>
                    <div></div>
                    <div className="text-right">TOTAL</div>
                    <div className="text-right whitespace-nowrap">
                      <span className="mr-0.5" style={{ color: ACCENT }}>{curPrefix}</span>{money(invoiceDetails.invoice.amount)}
                    </div>
                  </div>

                  {/* Row 6: Comments (left) + Payable (right) */}
                  <div className="grid grid-cols-[3fr_2fr] gap-4 px-4 py-3 mt-3 text-xs">
                    <div className="space-y-1">
                      <p className="font-bold text-sm">OTHER COMMENTS OR SPECIAL INSTRUCTIONS</p>
                      <p>1. Total payment due in {invoiceDetails.client.billing_cycle} days</p>
                      <p>2. Please include the invoice number on your wire transfer</p>
                      <p className="text-red-600">3. Overdue Payments are subject to an Interest Charge of 1.5% per month</p>
                    </div>
                    <div className="space-y-0.5">
                      <p>Make all checks payable to:</p>
                      <p className="font-bold">Milekal Inc.</p>
                    </div>
                  </div>

                  {/* Contact info (centered italic) */}
                  <div className="text-center italic text-xs px-4 py-3 mt-1">
                    <p>If you have any questions about this invoice, please contact</p>
                    <p>
                      {invoiceDetails.invoice.contact_person || 'Ratul'},&nbsp;&nbsp;&nbsp;
                      Tel:- {invoiceDetails.invoice.contact_phone || '+1 302-601-5286'},&nbsp;&nbsp;&nbsp;
                      E-mail: {invoiceDetails.invoice.contact_email || 'ratul@milekal.com'}
                    </p>
                  </div>

                  {/* Approved By / Date */}
                  <div className="grid grid-cols-[1fr_2fr_1fr] items-end gap-4 px-4 pt-6 text-xs italic">
                    <div className="border-b border-gray-500 pb-1">Approved By:&nbsp;&nbsp;{invoiceDetails.invoice.approved_by || ''}</div>
                    <div></div>
                    <div className="border-b border-gray-500 pb-1 text-right">Date:&nbsp;&nbsp;&nbsp;{format(new Date(invoiceDetails.invoice.issue_date), "dd-MMM-yy")}</div>
                  </div>

                  {/* Thank You */}
                  <div className="text-center italic font-bold text-lg mt-4" style={{ color: ACCENT }}>
                    Thank You For Your Business!
                  </div>

                  {/* Download Button */}
                  <div className="flex justify-end pt-4 mt-4 border-t">
                    <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => handleDownloadPDF(selectedInvoice.id)}>
                      <Download className="mr-2 h-4 w-4" />
                      Download PDF
                    </Button>
                  </div>
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>

        {/* Report Dialog */}
        <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Filtered Invoices Report</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                Showing {filteredInvoices.length} invoice(s) based on current filters
              </DialogDescription>
            </DialogHeader>
            
            <div className="mt-4">
              {filteredInvoices.length === 0 ? (
                <p className="text-md3-on-surface-variant text-center py-8">No invoices to display</p>
              ) : (
                <Table className="bg-md3-surface-container rounded-md3-md">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Project Name</TableHead>
                      <TableHead>Project Ref Invoice #</TableHead>
                      <TableHead>Invoice Date</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead>Last Reminder</TableHead>
                      <TableHead>Status</TableHead>
                      {canViewCosts() && <TableHead className="text-right">Invoice Amount</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredInvoices.map((invoice) => {
                      const project = projects.find(p => p.id === invoice.project_id);
                      return (
                        <TableRow key={invoice.id}>
                          <TableCell>{project?.name || '-'}</TableCell>
                          <TableCell>{invoice.project_reference_invoice_number || '-'}</TableCell>
                          <TableCell>{format(new Date(invoice.issue_date), "MMM dd, yyyy")}</TableCell>
                          <TableCell>{format(new Date(invoice.due_date), "MMM dd, yyyy")}</TableCell>
                          <TableCell>
                            {getLastReminderDate(invoice) ? (
                              <span className="text-md3-body-small">{format(new Date(getLastReminderDate(invoice)), "MMM dd, yyyy")}</span>
                            ) : (
                              <span className="text-md3-body-small text-gray-400">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className={`inline-block px-2 py-1 rounded text-xs font-semibold text-white ${getStatusColor(invoice.payment_status)}`}>
                              {formatPaymentStatus(invoice.payment_status)}
                            </span>
                          </TableCell>
                          {canViewCosts() && <TableCell className="text-right font-semibold">${invoice.amount.toLocaleString()}</TableCell>}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                  {canViewCosts() && filteredInvoices.length > 0 && (
                    <TableFooter>
                      <TableRow>
                        <TableCell colSpan={5} className="text-right font-bold">Total:</TableCell>
                        <TableCell className="text-right font-bold">
                          ${filteredInvoices.reduce((sum, inv) => sum + inv.amount, 0).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    </TableFooter>
                  )}
                </Table>
              )}
            </div>
            
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" onClick={() => setReportDialogOpen(false)}>
                Close
              </Button>
              <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => window.print()}>
                <FileText className="mr-2 h-4 w-4" /> Print Report
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

// ── Reusable filter combobox (used by Project-Invoice + Ready-to-Invoice tabs) ──
function FilterCombobox({ testid, label, placeholder, value, onChange, options }) {
  // options: [{ value, label, sublabel? }]
  const [open, setOpen] = useState(false);
  const selected = options.find(o => o.value === value);
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            className="w-full justify-between font-normal bg-white border-gray-300 h-9"
            data-testid={testid}
          >
            <span className={value ? "" : "text-md3-on-surface-variant"}>
              {selected ? selected.label : placeholder}
            </span>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
          <Command>
            <CommandInput placeholder="Type to search…" />
            <CommandList>
              <CommandEmpty>No match.</CommandEmpty>
              <CommandGroup>
                <CommandItem value="__all" onSelect={() => { onChange(""); setOpen(false); }}>
                  <Check className={`mr-2 h-4 w-4 ${!value ? "opacity-100" : "opacity-0"}`} />
                  {placeholder}
                </CommandItem>
                {options.map(o => (
                  <CommandItem
                    key={o.value}
                    value={o.label + (o.sublabel || "")}
                    onSelect={() => { onChange(o.value); setOpen(false); }}
                    className="whitespace-nowrap"
                  >
                    <Check className={`mr-2 h-4 w-4 shrink-0 ${value === o.value ? "opacity-100" : "opacity-0"}`} />
                    {o.sublabel && <span className="font-mono text-xs text-md3-primary mr-2 shrink-0">{o.sublabel}</span>}
                    <span className="truncate">{o.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

// ── Project-Invoice Tab ──────────────────────────────────────────
function ProjectInvoiceTab({ onOpenNewInvoice }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientFilter, setClientFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [amounts, setAmounts] = useState({});   // { project_id: "12345" }
  const [notes, setNotes] = useState({});
  const [saving, setSaving] = useState({});
  const [viewProject, setViewProject] = useState(null);
  const [onlyWithBalance, setOnlyWithBalance] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/invoices/project-summary");
      setRows(res.data.projects || []);
    } catch {
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = rows.filter(r => {
    if (clientFilter && r.client_name !== clientFilter) return false;
    if (projectFilter && r.project_id !== projectFilter) return false;
    if (onlyWithBalance && (Number(r.balance) || 0) <= 0) return false;
    return true;
  });
  const paged = usePagedList(filtered);

  const clientOptions = Array.from(new Set(
    rows
      .filter(r => !projectFilter || r.project_id === projectFilter)   // cascade: narrow by project too
      .map(r => r.client_name)
      .filter(Boolean)
  )).sort().map(name => ({ value: name, label: name }));
  const projectOptions = rows
    .filter(r => !clientFilter || r.client_name === clientFilter)   // cascade: narrow projects when a client is picked
    .slice()
    .sort((a, b) => (a.project_number || "").localeCompare(b.project_number || ""))
    .map(r => ({ value: r.project_id, label: r.project_name || "", sublabel: r.project_number || "" }));

  const fmt = (n, cur) => `${cur === "CAD" ? "C$" : "$"}${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  const release = async (r) => {
    const rawAmt = amounts[r.project_id];
    const amount = parseFloat(rawAmt);
    if (!amount || amount <= 0) { toast.error("Enter a valid amount"); return; }
    if (amount > r.balance + 1e-6) {
      toast.error(`Amount exceeds balance to invoice (${fmt(r.balance, r.currency)})`);
      return;
    }
    setSaving(s => ({ ...s, [r.project_id]: true }));
    try {
      await api.post("/proposed-invoices", {
        project_id: r.project_id,
        amount,
        note: notes[r.project_id] || null,
      });
      toast.success("Released to Ready-to-Invoice");
      setAmounts(a => ({ ...a, [r.project_id]: "" }));
      setNotes(n => ({ ...n, [r.project_id]: "" }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to release");
    } finally {
      setSaving(s => ({ ...s, [r.project_id]: false }));
    }
  };

  return (
    <div className="space-y-4" data-testid="pi-tab">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div className="flex items-end gap-4 flex-wrap">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:min-w-[520px]">
            <FilterCombobox
              testid="pi-filter-client"
              label="Client"
              placeholder="All clients"
              value={clientFilter}
              onChange={setClientFilter}
              options={clientOptions}
            />
            <FilterCombobox
              testid="pi-filter-project"
              label="Project"
              placeholder="All projects"
              value={projectFilter}
              onChange={setProjectFilter}
              options={projectOptions}
            />
          </div>
          <label
            htmlFor="pi-filter-balance"
            className="flex items-center gap-2 h-10 px-3 rounded-md3-md border border-md3-outline-variant bg-md3-surface-container hover:bg-md3-surface-container-high transition-colors cursor-pointer select-none"
            data-testid="pi-filter-balance-wrap"
          >
            <input
              id="pi-filter-balance"
              type="checkbox"
              checked={onlyWithBalance}
              onChange={(e) => setOnlyWithBalance(e.target.checked)}
              className="h-4 w-4 accent-md3-primary cursor-pointer"
              data-testid="pi-filter-balance"
            />
            <span className="text-sm text-md3-on-surface whitespace-nowrap">Balance to invoice only</span>
          </label>
        </div>
        <div className="text-xs text-md3-on-surface-variant">{filtered.length} of {rows.length} projects</div>
      </div>

      {loading ? (
        <div className="py-10 text-center text-md3-on-surface-variant">Loading…</div>
      ) : (
        <div className="overflow-auto rounded-md3-md border border-md3-outline-variant">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Project #</TableHead>
                <TableHead>Project Name</TableHead>
                <TableHead>Client</TableHead>
                <TableHead className="text-right">Base</TableHead>
                <TableHead className="text-right">CO</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Invoiced</TableHead>
                <TableHead className="text-right">Balance</TableHead>
                <TableHead className="w-[280px]">Propose Amount / Note</TableHead>
                <TableHead className="w-[160px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paged.pageItems.map(r => (
                <TableRow key={r.project_id} data-testid={`pi-row-${r.project_id}`}>
                  <TableCell className="font-mono text-xs text-md3-primary font-semibold whitespace-nowrap">{r.project_number || "—"}</TableCell>
                  <TableCell className="text-md3-on-surface">{r.project_name || "—"}</TableCell>
                  <TableCell>{r.client_name || "—"}</TableCell>
                  <TableCell className="text-right font-mono">{fmt(r.base, r.currency)}</TableCell>
                  <TableCell className="text-right font-mono">{fmt(r.co, r.currency)}</TableCell>
                  <TableCell className="text-right font-mono font-semibold">{fmt(r.total, r.currency)}</TableCell>
                  <TableCell className="text-right font-mono">{fmt(r.invoiced, r.currency)}</TableCell>
                  <TableCell className={`text-right font-mono font-semibold ${r.balance <= 0 ? "text-md3-on-surface-variant" : "text-emerald-700"}`}>
                    {fmt(r.balance, r.currency)}
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <Input
                        type="number"
                        step="0.01"
                        placeholder={`${r.currency} amount`}
                        value={amounts[r.project_id] || ""}
                        onChange={(e) => setAmounts(a => ({ ...a, [r.project_id]: e.target.value }))}
                        disabled={r.balance <= 0}
                        className="h-8 text-sm bg-white"
                        data-testid={`pi-amount-${r.project_id}`}
                      />
                      <Input
                        placeholder="Note (optional)"
                        value={notes[r.project_id] || ""}
                        onChange={(e) => setNotes(n => ({ ...n, [r.project_id]: e.target.value }))}
                        disabled={r.balance <= 0}
                        className="h-7 text-xs bg-white"
                        data-testid={`pi-note-${r.project_id}`}
                      />
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <Button
                        size="sm"
                        onClick={() => release(r)}
                        disabled={r.balance <= 0 || saving[r.project_id]}
                        data-testid={`pi-release-${r.project_id}`}
                      >
                        Release
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setViewProject(r)}
                        data-testid={`pi-view-${r.project_id}`}
                      >
                        View
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination
            currentPage={paged.page}
            totalItems={paged.total}
            pageSize={paged.pageSize}
            onPageChange={paged.onPageChange}
            onPageSizeChange={paged.onPageSizeChange}
            pageSizeOptions={[25, 50, 100]}
            testIdPrefix="project-invoice-pagination"
          />
        </div>
      )}

      <ProjectInvoiceSummarySheet
        open={!!viewProject}
        onClose={() => setViewProject(null)}
        row={viewProject}
      />
    </div>
  );
}

// ── Ready to Invoice Tab ─────────────────────────────────────────
function ReadyToInvoiceTab({ onGenerate, onInvoiced, refreshKey }) {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientFilter, setClientFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/proposed-invoices");
      setProposals(res.data.proposals || []);
    } catch {
      toast.error("Failed to load proposals");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [refreshKey]);

  const filtered = proposals.filter(r => {
    if (clientFilter && r.client_name !== clientFilter) return false;
    if (projectFilter && r.project_id !== projectFilter) return false;
    return true;
  });
  const paged = usePagedList(filtered);

  const clientOptions = Array.from(new Set(
    proposals
      .filter(r => !projectFilter || r.project_id === projectFilter)
      .map(r => r.client_name)
      .filter(Boolean)
  )).sort().map(name => ({ value: name, label: name }));
  const projectOptions = Array.from(
    proposals
      .filter(r => !clientFilter || r.client_name === clientFilter)   // cascading
      .reduce((m, r) => m.set(r.project_id, r), new Map())
      .values()
  )
    .sort((a, b) => (a.project_number || "").localeCompare(b.project_number || ""))
    .map(r => ({ value: r.project_id, label: r.project_name || "", sublabel: r.project_number || "" }));

  const fmt = (n, cur) => `${cur === "CAD" ? "C$" : "$"}${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  const deleteProposal = async (id) => {
    if (!window.confirm("Delete this proposal?")) return;
    try {
      await api.delete(`/proposed-invoices/${id}`);
      toast.success("Proposal deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const generate = (r) => {
    // Pre-fill the New Invoice dialog with this proposal's data.
    // proposal_id is passed so the parent can auto-delete on successful save.
    onGenerate({
      proposal_id: r.id,
      project_id: r.project_id,
      invoice_items: [{ description: r.note || "", amount: String(r.amount) }],
      notes: r.note || "",
    });
  };

  return (
    <div className="space-y-4" data-testid="rti-tab">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 w-full md:max-w-xl">
          <FilterCombobox
            testid="rti-filter-client"
            label="Client"
            placeholder="All clients"
            value={clientFilter}
            onChange={setClientFilter}
            options={clientOptions}
          />
          <FilterCombobox
            testid="rti-filter-project"
            label="Project"
            placeholder="All projects"
            value={projectFilter}
            onChange={setProjectFilter}
            options={projectOptions}
          />
        </div>
        <div className="text-xs text-md3-on-surface-variant">{filtered.length} of {proposals.length} pending</div>
      </div>

      {loading ? (
        <div className="py-10 text-center text-md3-on-surface-variant">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="py-10 text-center text-md3-on-surface-variant">No pending proposals. Use the Project-Invoice tab to release amounts.</div>
      ) : (
        <div className="overflow-auto rounded-md3-md border border-md3-outline-variant">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Project</TableHead>
                <TableHead>Client</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Invoiced</TableHead>
                <TableHead className="text-right">Balance</TableHead>
                <TableHead className="text-right">This Invoice</TableHead>
                <TableHead>Note</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paged.pageItems.map(r => (
                <TableRow key={r.id} data-testid={`rti-row-${r.id}`}>
                  <TableCell className="font-mono text-xs">
                    <span className="text-md3-primary font-semibold">{r.project_number || "—"}</span>
                    <span className="text-md3-on-surface-variant"> · {r.project_name || "—"}</span>
                  </TableCell>
                  <TableCell>{r.client_name || "—"}</TableCell>
                  <TableCell className="text-right font-mono">{fmt(r.total, r.currency)}</TableCell>
                  <TableCell className="text-right font-mono">{fmt(r.invoiced, r.currency)}</TableCell>
                  <TableCell className="text-right font-mono">{fmt(r.balance, r.currency)}</TableCell>
                  <TableCell className="text-right font-mono font-semibold text-emerald-700">{fmt(r.amount, r.currency)}</TableCell>
                  <TableCell className="text-xs text-md3-on-surface-variant max-w-[200px] truncate" title={r.note || ""}>{r.note || "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" onClick={() => generate(r)} data-testid={`rti-generate-${r.id}`}>Generate Invoice</Button>
                      <Button size="sm" variant="outline" onClick={() => deleteProposal(r.id)} data-testid={`rti-delete-${r.id}`}>Delete</Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination
            currentPage={paged.page}
            totalItems={paged.total}
            pageSize={paged.pageSize}
            onPageChange={paged.onPageChange}
            onPageSizeChange={paged.onPageSizeChange}
            pageSizeOptions={[25, 50, 100]}
            testIdPrefix="ready-invoice-pagination"
          />
        </div>
      )}
    </div>
  );
}

// ── Slide-out invoice summary sheet ───────────────────────────────
function ProjectInvoiceSummarySheet({ open, onClose, row }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [canInvoice, setCanInvoice] = useState(null); // computed on-demand for THIS project only

  useEffect(() => {
    if (!open || !row?.project_id) { setDetail(null); setCanInvoice(null); return; }
    (async () => {
      setLoading(true);
      try {
        // Fetch invoices for this project + balance breakdown (for Can Invoice)
        const [invRes, balRes] = await Promise.all([
          api.get("/invoices", { params: { project_id: row.project_id } }),
          api.get(`/projects/${row.project_id}/invoice-balance`).catch(() => null),
        ]);
        setDetail(invRes.data || []);
        // Can Invoice = (% completion × total_detailing) − total_invoiced
        if (balRes?.data) {
          const b = balRes.data;
          const pct = Number(b.project_completion_percentage || 0);
          const td = Number(b.breakdown?.total_detailing || 0);
          const inv = Number(b.total_invoiced || 0);
          setCanInvoice(Math.round(((pct / 100) * td - inv) * 100) / 100);
        } else {
          setCanInvoice(0);
        }
      } catch {
        toast.error("Failed to load invoice history");
      } finally {
        setLoading(false);
      }
    })();
  }, [open, row?.project_id]);

  const fmt = (n) => `${row?.currency === "CAD" ? "C$" : "$"}${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{row?.project_number} · {row?.project_name}</SheetTitle>
        </SheetHeader>
        {!row ? null : (
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-md3-on-surface-variant">Client:</span> <span className="font-medium">{row.client_name || "—"}</span></div>
              <div><span className="text-md3-on-surface-variant">Currency:</span> <span className="font-medium">{row.currency}</span></div>
              <div><span className="text-md3-on-surface-variant">Base:</span> <span className="font-mono">{fmt(row.base)}</span></div>
              <div><span className="text-md3-on-surface-variant">Change Orders:</span> <span className="font-mono">{fmt(row.co)}</span></div>
              <div><span className="text-md3-on-surface-variant">Total:</span> <span className="font-mono font-semibold">{fmt(row.total)}</span></div>
              <div><span className="text-md3-on-surface-variant">Invoiced:</span> <span className="font-mono">{fmt(row.invoiced)}</span></div>
              <div><span className="text-md3-on-surface-variant">Proposed (in queue):</span> <span className="font-mono">{fmt(row.proposed)}</span></div>
              <div><span className="text-md3-on-surface-variant">Balance to invoice:</span> <span className={`font-mono font-semibold ${row.balance <= 0 ? "" : "text-emerald-700"}`}>{fmt(row.balance)}</span></div>
              <div
                title="(% completion × Detailing) − Already Invoiced"
                data-testid="pi-sheet-can-invoice"
              >
                <span className="text-md3-on-surface-variant">Can invoice:</span>{" "}
                <span className={`font-mono font-semibold ${(canInvoice || 0) <= 0 ? "" : "text-blue-700"}`}>
                  {canInvoice === null ? "…" : fmt(canInvoice)}
                </span>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-md3-title-medium mb-2">Invoices ({detail?.length || 0})</h4>
              {loading ? (
                <div className="text-md3-on-surface-variant text-sm">Loading…</div>
              ) : (detail || []).length === 0 ? (
                <div className="text-md3-on-surface-variant text-sm">No invoices yet.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>Issue Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(detail || []).map(inv => (
                      <TableRow key={inv.id}>
                        <TableCell className="font-mono text-xs">{inv.invoice_number}</TableCell>
                        <TableCell className="text-xs">{(inv.issue_date || inv.created_at || "").slice(0, 10)}</TableCell>
                        <TableCell><Badge variant="outline" className="text-xs capitalize">{(inv.payment_status || "—").replace(/_/g, " ")}</Badge></TableCell>
                        <TableCell className="text-right font-mono">{fmt(inv.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
